import { biVizLook } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizLook';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiQueryResult } from '@euler/model/bi/result';
import {
  BiPalette,
  BiVizSpec,
  BiVizStyle,
  BiVizStyleOption,
  BiVizType,
  biVizStyleOptionsFor,
  isBiChartViz,
} from '@euler/model/bi/viz';
import { ReactNode, memo, useMemo } from 'react';
import { View } from 'react-native';
import { biColorTargets } from './functions/biColorTargets';
import { AxesOptions } from './options/AxesOptions';
import { BarOptions } from './options/BarOptions';
import { FlagOption } from './options/FlagOption';
import { FunnelOptions } from './options/FunnelOptions';
import { KpiOptions } from './options/KpiOptions';
import { LegendOptions } from './options/LegendOptions';
import { LineOptions } from './options/LineOptions';
import { PaletteOptions } from './options/PaletteOptions';
import { PieOptions } from './options/PieOptions';
import { RadarOptions } from './options/RadarOptions';
import { SeriesColorOptions } from './options/SeriesColorOptions';
import { SubtitleOptions } from './options/SubtitleOptions';
import { TableOptions } from './options/TableOptions';
import { TitleOptions } from './options/TitleOptions';

/**
 * The look of the card: only the options its chart type takes
 * (`biVizStyleOptionsFor`), so switching type hides the rest — and the
 * saved viz drops them.
 */
export const StyleSection = memo(
  ({
    viz,
    style,
    result,
    dashboardPalette,
    onChange,
  }: {
    /** The viz as it would save; null while the form cannot preview. */
    viz: BiVizSpec | null;
    /** Everything set so far, fitting options or not. */
    style: BiVizStyle | undefined;
    /** The preview's rows, to name the series and slices to color. */
    result: BiQueryResult | null;
    dashboardPalette: BiPalette | undefined;
    onChange: (style: BiVizStyle | undefined) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const options = useMemo(
      () => (viz ? biVizStyleOptionsFor(viz) : []),
      [viz],
    );
    const targets = useMemo(
      () =>
        viz && isBiChartViz(viz)
          ? biColorTargets(viz, result, dashboardPalette)
          : [],
      [viz, result, dashboardPalette],
    );
    const props = { style, onChange };
    const render = (option: BiVizStyleOption): ReactNode => {
      if (!viz) return null;
      switch (option) {
        case 'title':
          return <TitleOptions {...props} />;
        case 'subtitle':
          return <SubtitleOptions {...props} />;
        case 'palette':
          return (
            <PaletteOptions {...props} dashboardPalette={dashboardPalette} />
          );
        case 'seriesColors':
          return <SeriesColorOptions {...props} targets={targets} />;
        case 'legend':
          return <LegendOptions {...props} />;
        case 'axes':
          return <AxesOptions {...props} viz={viz} />;
        case 'gridLines':
          return <FlagOption {...props} option={option} />;
        case 'dataLabels':
          return (
            <FlagOption
              {...props}
              option={option}
              fallback={biVizLook({ ...viz, style: undefined }).dataLabels}
            />
          );
        case 'line':
          return <LineOptions {...props} />;
        case 'bar':
          return <BarOptions {...props} />;
        case 'pie':
          return viz.type === BiVizType.Pie || viz.type === BiVizType.Donut ? (
            <PieOptions {...props} type={viz.type} />
          ) : null;
        case 'radar':
          return <RadarOptions {...props} />;
        case 'funnel':
          return <FunnelOptions {...props} />;
        case 'table':
          return <TableOptions {...props} />;
        case 'kpi':
          return <KpiOptions {...props} />;
      }
    };
    return (
      <View style={{ gap: rpx(4) }}>
        <Typography.Text medium size={rpx(13)} color={aiColors.foreground}>
          样式
        </Typography.Text>
        {viz ? (
          options.map(option => <View key={option}>{render(option)}</View>)
        ) : (
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            完成上面的设置后可以调整样式。
          </Typography.Text>
        )}
      </View>
    );
  },
);
